/**
 * Resolve role labels / colors from the login-loaded catalog.
 * Use this everywhere instead of hardcoded "Tuteur" / "Élève" / "VSC" maps.
 */

import { SUPPORT_TYPE_LABEL } from '@/constants/shared-catalog';
import { useRoleCatalogStore } from '@/stores/roleCatalogStore';
import type { Role, RoleMode } from '@/types/role.types';

/** Minimal role shape for code lookup (API / users.service and role.types). */
export interface RoleCodeMatch {
  id: string;
  code: string;
  label: string;
}

/**
 * Last-resort labels before the catalog is loaded (or for non-DB codes
 * such as LiveKit `supervisor` / `animator`). After login, DB labels win.
 */
const FALLBACK_ROLE_LABELS: Record<string, string> = {
  admin: 'Administrateur',
  manager: 'Manager',
  manager_remote: 'Manager',
  coordinator: 'Coordinateur',
  coordinator_remote: 'Coordinateur',
  recruiter: 'Recruteur',
  recruiter_remote: 'Recruteur',
  parent: 'Parent',
  parent_remote: 'Parent',
  student: 'Élève',
  student_remote: 'Élève',
  tutor: 'Tuteur',
  tutor_remote: 'Tuteur',
  professor: 'Professeur',
  volunteer: 'VSC',
  supervisor: 'Superviseur',
  animator: 'Animateur',
};

/** Normalize a role code for catalog lookup (`TUTOR` → `tutor`). */
export function normalizeRoleCode(code: string): string {
  return code.trim().toLowerCase();
}

/** Base role name without a mode suffix (`tutor_remote` → `tutor`). */
export function roleKind(code: string | null | undefined): string {
  if (!code) return '';
  return normalizeRoleCode(code).replace(/_remote$|_hybrid$|_in_person$/, '');
}

/** Role mode implied by a `_remote` / `_hybrid` / `_in_person` code suffix. */
function modeFromSuffix(code: string): RoleMode | null {
  const normalized = normalizeRoleCode(code);
  if (normalized.endsWith('_remote')) return 'remote';
  if (normalized.endsWith('_hybrid')) return 'hybrid';
  if (normalized.endsWith('_in_person')) return 'in_person';
  return null;
}

/** Mode from a `_remote` / `_in_person` suffix, else catalog `mode`. */
export function roleMode(
  code: string | null | undefined,
  catalogMode?: RoleMode | null,
): RoleMode | null {
  if (!code) return catalogMode ?? null;
  const fromSuffix = modeFromSuffix(code);
  if (fromSuffix) return fromSuffix;
  if (catalogMode != null) return catalogMode;
  return getRoleByCode(normalizeRoleCode(code))?.mode ?? null;
}

/** When the same role exists in two modes, add the mode in parentheses. */
export function formatRoleByMode(
  options: Array<{ value: string; label: string; code: string }>,
): Array<{ value: string; label: string }> {
  const kindCounts = new Map<string, number>();
  for (const option of options) {
    const kind = roleKind(option.code);
    kindCounts.set(kind, (kindCounts.get(kind) ?? 0) + 1);
  }
  return options.map((option) => {
    const kind = roleKind(option.code);
    if ((kindCounts.get(kind) ?? 0) < 2) {
      return { value: option.value, label: option.label };
    }
    const mode = roleMode(option.code);
    const modeLabel = mode ? SUPPORT_TYPE_LABEL[mode] : undefined;
    if (!modeLabel) return { value: option.value, label: option.label };
    return { value: option.value, label: `${option.label} (${modeLabel})` };
  });
}

/** True when `code` is `kind`, optionally restricted to a mode. */
export function roleMatches(
  code: string | null | undefined,
  kind: string,
  mode?: RoleMode | null,
): boolean {
  if (!code || !kind) return false;
  if (roleKind(code) !== roleKind(kind)) return false;
  if (mode == null) return true;
  return roleMode(code) === mode;
}

/** Match role code case-insensitively (config targetRole may be PROFESSOR, API code professor). */
export function findRoleByCode(
  roles: RoleCodeMatch[],
  code: string | undefined,
): RoleCodeMatch | undefined {
  if (!code?.trim()) return undefined;
  const needle = code.trim().toLowerCase();
  return roles.find((r) => r.code.toLowerCase() === needle);
}

/** Candidate catalog keys: exact, then `_remote` / `_in_person` aliases. */
function catalogLookupKeys(code: string): string[] {
  const keys = [code];
  if (code.endsWith('_remote')) {
    keys.push(code.slice(0, -'_remote'.length));
  } else {
    keys.push(`${code}_remote`);
  }
  if (code.endsWith('_in_person')) {
    keys.push(code.slice(0, -'_in_person'.length));
  }
  return keys;
}

/** Catalog row for a role code, including remote / in-person aliases. */
export function getRoleByCode(
  code: string | null | undefined,
  mode?: RoleMode | null,
): Role | undefined {
  if (!code) return undefined;
  const store = useRoleCatalogStore.getState();
  const normalized = normalizeRoleCode(code);
  const kind = roleKind(normalized);
  const wantedMode = mode ?? modeFromSuffix(normalized);

  if (wantedMode) {
    const byKindAndMode = store.roles.find(
      (role) =>
        roleKind(role.code) === kind &&
        (role.mode === wantedMode || modeFromSuffix(role.code) === wantedMode),
    );
    if (byKindAndMode) return byKindAndMode;
  }

  for (const key of catalogLookupKeys(normalized)) {
    const role = store.byCode[key];
    if (role) return role;
  }
  return undefined;
}

/** Catalog row by role id. */
export function getRoleById(id: string | null | undefined): Role | undefined {
  if (!id) return undefined;
  return useRoleCatalogStore.getState().byId[id];
}

/**
 * Display label for a role code.
 * Prefers the login catalog, then a static fallback, then the raw code.
 */
export function getRoleLabel(code: string | null | undefined, fallback?: string): string {
  if (!code) return fallback ?? 'Inconnu';

  const normalized = normalizeRoleCode(code);
  if (normalized === 'volunteer_and_coordinator') {
    return `${getRoleLabel('volunteer')} ou son ${getRoleLabel('coordinator').toLowerCase()}`;
  }

  const role = getRoleByCode(normalized);
  if (role?.label) return role.label;

  return FALLBACK_ROLE_LABELS[normalized] ?? fallback ?? code;
}

/** CSS color for a role (`color-1-nuance-1` → `var(--color-1-nuance-1)`). */
export function toRoleColorCss(
  color: string | null | undefined,
  fallback = 'var(--color-1-nuance-1)',
): string {
  if (!color) return fallback;
  if (color.startsWith('color-')) return `var(--${color})`;
  if (color.startsWith('#') || color.startsWith('rgb') || color.startsWith('var(')) return color;
  return fallback;
}

/** CSS color from the catalog, then fallback. */
export function getRoleColorCss(code: string | null | undefined, fallback?: string): string {
  const role = getRoleByCode(code);
  return toRoleColorCss(role?.color, fallback);
}

/** `{ value, label }` options for a fixed list of role codes (pickers / filters). */
export function getRoleLabelOptions(
  codes: readonly string[],
): Array<{ value: string; label: string }> {
  return codes.map((value) => ({ value, label: getRoleLabel(value) }));
}

/**
 * Plural display label (`Tuteur` → `Tuteurs`). Acronyms like `VSC` stay unchanged.
 */
export function getRoleLabelPlural(code: string | null | undefined): string {
  const label = getRoleLabel(code);
  if (!label || label === 'Inconnu') return label;
  if (label === label.toUpperCase()) return label;
  if (/[sx]$/i.test(label)) return label;
  return `${label}s`;
}
